import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Page, Stepper } from '../components/Layout'
import { ErrorBox } from '../components/ui'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabase'
import { apiPost } from '../lib/api'

interface Documento {
  id: string
  tipo: string
  nome_arquivo: string | null
  status: string
}

const CHECKLIST = [
  { tipo: 'documento_pessoal', label: 'Documento pessoal — RG/CPF ou CNH (de todos os compradores)' },
  { tipo: 'comprovante_residencia', label: 'Comprovante de residência' },
  { tipo: 'contrato_compra', label: 'Contrato de compra e venda / promessa (com a construtora)' },
  { tipo: 'contrato_financiamento', label: 'Contrato de financiamento (Caixa/banco), se houver' },
  { tipo: 'comprovantes_pagamento', label: 'Comprovantes de pagamento / extrato das parcelas (construtora e banco)' },
  { tipo: 'fotos_imovel', label: 'Fotos do imóvel / da obra (mostrando o atraso)' },
  { tipo: 'comunicacoes', label: 'Conversas com a construtora (incl. pedido de distrato, se houve)' },
]

const BUCKET = 'documentos-casos'

export default function PortalDocumentos() {
  const { user, loading } = useAuth()
  const [casoId, setCasoId] = useState<string | null>(null)
  const [docs, setDocs] = useState<Documento[]>([])
  const [erro, setErro] = useState<string | null>(null)
  const [enviando, setEnviando] = useState<string | null>(null)
  const fileRefs = useRef<Record<string, HTMLInputElement | null>>({})

  async function carregar() {
    const { data: casos, error: e1 } = await supabase
      .from('casos')
      .select('id')
      .order('created_at', { ascending: false })
      .limit(1)
    if (e1) {
      setErro(e1.message)
      return
    }
    const cid = casos?.[0]?.id ?? null
    setCasoId(cid)
    if (cid) {
      const { data, error: e2 } = await supabase
        .from('documentos')
        .select('id, tipo, nome_arquivo, status')
        .eq('caso_id', cid)
      if (e2) {
        setErro(e2.message)
        return
      }
      setDocs((data as Documento[]) ?? [])
    }
  }

  useEffect(() => {
    if (!loading && user) carregar()
  }, [loading, user])

  async function enviarArquivo(tipo: string, file: File) {
    if (!casoId) return
    setErro(null)
    setEnviando(tipo)
    try {
      const up = await apiPost<{ documento_id: string; path: string; token: string }>(
        '/api/documentos/upload-url',
        { caso_id: casoId, tipo, nome_arquivo: file.name, mime_type: file.type },
        { auth: true },
      )
      const { error } = await supabase.storage.from(BUCKET).uploadToSignedUrl(up.path, up.token, file)
      if (error) throw error
      await apiPost('/api/documentos/confirmar', { documento_id: up.documento_id }, { auth: true })
      await carregar()
    } catch (e) {
      setErro((e as Error).message)
    } finally {
      setEnviando(null)
    }
  }

  if (loading) return <Page><p className="text-pfa-slate">Carregando…</p></Page>
  if (!user)
    return (
      <Page>
        <div className="card">
          <p className="text-pfa-slate">Você precisa ativar e entrar na sua conta.</p>
          <Link to="/painel" className="btn-primary mt-4">Entrar</Link>
        </div>
      </Page>
    )

  function statusDe(tipo: string): string {
    return docs.find((d) => d.tipo === tipo)?.status ?? 'pendente'
  }

  return (
    <Page>
      <Stepper step={3} />
      <h1 className="text-2xl font-semibold text-pfa-navy">Seus documentos</h1>
      <p className="mt-2 text-pfa-slate">Envie os documentos abaixo para darmos andamento ao seu caso.</p>
      <ErrorBox message={erro} />

      <div className="mt-6 space-y-3">
        {CHECKLIST.map((item) => {
          const st = statusDe(item.tipo)
          const ok = st === 'recebido' || st === 'validado'
          return (
            <div key={item.tipo} className="card flex items-center justify-between">
              <div>
                <p className="font-medium text-pfa-navy">{item.label}</p>
                <p className="text-xs text-pfa-slate/70">
                  {st === 'validado' ? 'Validado' : st === 'recebido' ? 'Recebido' : 'Pendente'}
                </p>
              </div>
              <div>
                <input
                  ref={(el) => (fileRefs.current[item.tipo] = el)}
                  type="file"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0]
                    if (f) enviarArquivo(item.tipo, f)
                  }}
                />
                <button
                  className={ok ? 'btn-ghost' : 'btn-primary'}
                  disabled={enviando === item.tipo}
                  onClick={() => fileRefs.current[item.tipo]?.click()}
                >
                  {enviando === item.tipo ? 'Enviando…' : ok ? 'Reenviar' : 'Enviar'}
                </button>
              </div>
            </div>
          )
        })}
      </div>
    </Page>
  )
}
